import React, { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import MenuIcon from '@mui/icons-material/Menu'
import {
  Badge,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import moment from 'moment'
import { EPCRContext } from './EPCR'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle'
import CloudDoneIcon from '@mui/icons-material/CloudDone'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import { getFormType, isFullLayoutMode } from './formTypes'
import FavoriteIcon from '@mui/icons-material/Favorite'
import BoltIcon from '@mui/icons-material/Bolt'

export const syncStatusColors = (variable) => {
  switch (variable) {
    case 'error':
      return '#E32525'
    case 'done':
      return '#2e7d32'
    default:
      return '#ed6c02'
  }
}

const EPCRHeader = ({ ptDetails, epcrMode, epcrNumber }) => {
  const { setSidebarState, epcrPracticeMode } = useContext(SiteModeContext)
  const {
    uploadState,
    downloadState,
    setFormTypeDialogOpen,
    selectedEPCRModule,
    setSelectedEPCRModule,
  } = useContext(EPCRContext)
  const fullLayout = isFullLayoutMode(epcrMode)
  const formType = getFormType(epcrMode)
  const [currentTime, setCurentTime] = useState(moment())

  useEffect(() => {
    const timer = setInterval(() => setCurentTime(moment()), 1000)
    return () => clearInterval(timer)
  }, [])

  const uploadIconSx = {
    width: '1.15rem',
    height: '1.15rem',
    color: () => syncStatusColors(uploadState),
  }

  const downloadIconSx = {
    width: '1.15rem',
    height: '1.15rem',
    color: () => syncStatusColors(downloadState),
  }

  return (
    <Box
      display="flex"
      gap={1}
      component={Paper}
      elevation={0}
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        border: epcrPracticeMode
          ? '1px solid #990000'
          : '1px solid rgba(15,23,42,0.08)',
        backgroundColor: epcrPracticeMode ? '#990000' : '#fff',
        color: epcrPracticeMode ? '#fff' : '#0F172A',
      }}
    >
      <Box display="flex" gap={1} alignItems="center" minWidth={0}>
        {!fullLayout ? (
          <Tooltip title="Change Record Type" sx={{ cursor: 'pointer' }}>
            <ChangeCircleIcon onClick={() => setFormTypeDialogOpen(true)} />
          </Tooltip>
        ) : (
          <MenuIcon
            onClick={() =>
              setSidebarState((prev) => ({ ...prev, mobileOpen: true }))
            }
            sx={{
              display: { xs: 'block', lg: 'none' },
              cursor: 'pointer',
            }}
          />
        )}
        {!epcrPracticeMode && epcrMode !== 'ePCR' && (
          <Chip
            size="small"
            label={formType.label}
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              fontWeight: 700,
              backgroundColor: formType.bg ?? 'rgba(15,23,42,0.06)',
              color: formType.color,
            }}
          />
        )}
        {epcrPracticeMode && (
          <Chip
            size="small"
            label="PRACTICE MODE"
            sx={{
              fontWeight: 800,
              letterSpacing: '0.06em',
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#fff',
            }}
          />
        )}
        <Typography
          noWrap
          sx={{
            fontWeight: 700,
            fontSize: 15,
            color: 'inherit',
            minWidth: 0,
          }}
        >
          {epcrPracticeMode
            ? 'No data is saved in practice mode'
            : fullLayout
              ? ptDetails?.trim() || 'New patient'
              : (formType.headerTitle ?? ptDetails)}
        </Typography>
      </Box>
      <Box display="flex" gap={{ xs: 1, md: 1.5 }} alignItems="center">
        {epcrMode === 'Critical Care' && selectedEPCRModule !== 'Live' && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<BoltIcon />}
            onClick={() => setSelectedEPCRModule('Live')}
            sx={{
              py: 0.25,
              whiteSpace: 'nowrap',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              color: epcrPracticeMode ? '#fff' : formType.color,
              borderColor: epcrPracticeMode
                ? 'rgba(255,255,255,0.5)'
                : `${formType.color}55`,
              '&:hover': {
                borderColor: epcrPracticeMode ? '#fff' : formType.color,
                backgroundColor: epcrPracticeMode
                  ? 'rgba(255,255,255,0.08)'
                  : (formType.bg ?? 'rgba(15,23,42,0.04)'),
              },
            }}
          >
            Live console
          </Button>
        )}
        {fullLayout && selectedEPCRModule !== 'Arrest' && (
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<FavoriteIcon />}
            onClick={() => setSelectedEPCRModule('Arrest')}
            sx={{
              py: 0.25,
              whiteSpace: 'nowrap',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            <Box component="span" display={{ xs: 'none', sm: 'inline' }}>
              Cardiac&nbsp;
            </Box>
            arrest
          </Button>
        )}
        <Typography
          sx={{
            display: { xs: 'none', lg: 'inherit' },
            fontFamily: 'ui-monospace, monospace',
            fontSize: 13.5,
            color: epcrPracticeMode ? 'rgba(255,255,255,0.85)' : '#475569',
          }}
        >
          {moment(currentTime).format('LTS')}
        </Typography>
        {!epcrPracticeMode && (
          <Box
            display="flex"
            gap={0.75}
            alignItems="center"
            sx={{
              px: 1,
              py: 0.4,
              borderRadius: 999,
              backgroundColor: 'rgba(15,23,42,0.04)',
            }}
          >
            <Tooltip title="Upload status">
              {uploadState === 'done' ? (
                <CloudDoneIcon sx={uploadIconSx} />
              ) : uploadState === 'pending' ? (
                <CloudUploadIcon sx={uploadIconSx} />
              ) : (
                <CloudOffIcon sx={uploadIconSx} />
              )}
            </Tooltip>
            <Tooltip title="Download status">
              {downloadState === 'done' ? (
                <CloudDoneIcon sx={downloadIconSx} />
              ) : downloadState === 'pending' ? (
                <CloudDownloadIcon sx={downloadIconSx} />
              ) : (
                <CloudOffIcon sx={downloadIconSx} />
              )}
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default EPCRHeader
